import { APIClient } from '@customerio/hydra-ui-api';
import {
  useMutation as useTanStackMutation,
  useQuery as useTanStackQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { getApiBaseUrl } from './base-url';
import { getAuthHeaders } from './auth-headers';
import { shouldRetry } from './retry';
import { recordRumRequest } from '@/services/rum';

/**
 * Pipeline Source Types
 */

export interface PipelineSource {
  id: number;
  name: string;
  option_id: string;
  enabled: boolean;
  has_cio_sources: boolean;
}

export interface PipelineSourceOption {
  option_id: string;
  name: string;
  icon: string;
  category: string;
  type: 'api' | 'connector' | 'database';
  hidden: boolean;
  code?: string;
  language?: string;
  library_install_command?: string;
  library_install_instructions?: string;
  library_install_learn_more_url?: string;
}

export interface PipelineApiKey {
  id: number;
  api_key: string;
  created_by: number;
  source_id: number;
  created_at: number;
  /** CDP's kind for the key. */
  kind: 'write_key' | 'public' | 'secret';
}

/**
 * Route definitions for the Pipelines (CDP) backend (`/cdp/api`). It has no
 * OpenAPI spec, so this hand-written `paths`-shaped type gives the shared
 * `APIClient` transport the same type safety the generated UI API client gets
 * (path params, request body, and response are all inferred). Path templates
 * use `{name}` placeholders.
 */
interface CdpPaths {
  '/workspaces/{workspaceId}/sources': {
    get: {
      parameters: { path: { workspaceId: number } };
      responses: { 200: { content: { 'application/json': { sources: PipelineSource[] } } } };
    };
    post: {
      parameters: { path: { workspaceId: number } };
      requestBody: { content: { 'application/json': { source: { name: string; option_id: string } } } };
      responses: { 200: { content: { 'application/json': PipelineSource } } };
    };
  };
  '/workspaces/{workspaceId}/available_sources': {
    get: {
      parameters: { path: { workspaceId: number } };
      responses: { 200: { content: { 'application/json': { sources: PipelineSourceOption[] } } } };
    };
  };
  '/workspaces/{workspaceId}/sources/statuses': {
    get: {
      parameters: { path: { workspaceId: number } };
      responses: { 200: { content: { 'application/json': { statuses: Record<string, number> } } } };
    };
  };
  '/workspaces/{workspaceId}/sources/api_keys': {
    get: {
      parameters: { path: { workspaceId: number } };
      responses: { 200: { content: { 'application/json': { api_keys: PipelineApiKey[] } } } };
    };
  };
  '/workspaces/{workspaceId}/sources/{sourceId}/api_keys': {
    get: {
      parameters: { path: { workspaceId: number; sourceId: number } };
      responses: { 200: { content: { 'application/json': { api_keys: PipelineApiKey[] } } } };
    };
    post: {
      parameters: { path: { workspaceId: number; sourceId: number } };
      responses: { 200: { content: { 'application/json': PipelineApiKey } } };
    };
  };
  '/workspaces/{workspaceId}/sources/{sourceId}/api_keys/{apiKeyId}': {
    delete: {
      parameters: { path: { workspaceId: number; sourceId: number; apiKeyId: number } };
      responses: { 200: { content: { 'application/json': unknown } } };
    };
  };
}

/**
 * A typed client for the Pipelines (CDP) backend over a hand-written `paths`
 * type. `timeout` is in milliseconds; 0 turns the client's 30 s default off,
 * which callers whose requests can outlast it (a warehouse query) need.
 */
export function createCdpApiClient<Paths>({ timeout }: { timeout?: number } = {}) {
  return new APIClient<Paths>({
    // The Pipelines backend is reached under `/cdp/api` on the regional Journeys
    // host, so prefix the region/env base URL (resolved per request) — matching
    // the previous ts-rest client, which routed through `getApiBaseUrl()`.
    baseUrl: () => `${getApiBaseUrl()}/cdp/api`,
    fetchOptions: { credentials: 'include' },
    headers: getAuthHeaders,
    onRequestTiming: recordRumRequest,
    timeout,
  });
}

const cdpClient = createCdpApiClient<CdpPaths>();

/** @public */
export const cdpApi = {
  listSources: (args: { params: { workspaceId: number } }) =>
    cdpClient.get('/workspaces/{workspaceId}/sources', { params: { path: { workspaceId: args.params.workspaceId } } }),
  createSource: (args: { params: { workspaceId: number }; body: { source: { name: string; option_id: string } } }) =>
    cdpClient.post('/workspaces/{workspaceId}/sources', {
      params: { path: { workspaceId: args.params.workspaceId } },
      body: args.body,
    }),
  listSourceOptions: (args: { params: { workspaceId: number } }) =>
    cdpClient.get('/workspaces/{workspaceId}/available_sources', {
      params: { path: { workspaceId: args.params.workspaceId } },
    }),
  /** Every source's keys in the workspace, in one call. */
  listWorkspaceApiKeys: (args: { params: { workspaceId: number } }) =>
    cdpClient.get('/workspaces/{workspaceId}/sources/api_keys', {
      params: { path: { workspaceId: args.params.workspaceId } },
    }),
  listSourceApiKeys: (args: { params: { workspaceId: number; sourceId: number } }) =>
    cdpClient.get('/workspaces/{workspaceId}/sources/{sourceId}/api_keys', {
      params: { path: { workspaceId: args.params.workspaceId, sourceId: args.params.sourceId } },
    }),
  createSourceApiKey: (args: { params: { workspaceId: number; sourceId: number } }) =>
    cdpClient.post('/workspaces/{workspaceId}/sources/{sourceId}/api_keys', {
      params: { path: { workspaceId: args.params.workspaceId, sourceId: args.params.sourceId } },
    }),
  getSourceStatuses: (args: { params: { workspaceId: number } }) =>
    cdpClient.get('/workspaces/{workspaceId}/sources/statuses', {
      params: { path: { workspaceId: args.params.workspaceId } },
    }),
  deleteSourceApiKey: (args: { params: { workspaceId: number; sourceId: number; apiKeyId: number } }) =>
    cdpClient.delete('/workspaces/{workspaceId}/sources/{sourceId}/api_keys/{apiKeyId}', {
      params: {
        path: {
          workspaceId: args.params.workspaceId,
          sourceId: args.params.sourceId,
          apiKeyId: args.params.apiKeyId,
        },
      },
    }),
};

type CdpApi = typeof cdpApi;
type CdpArgs<M extends keyof CdpApi> = Parameters<CdpApi[M]>[0];
type CdpData<M extends keyof CdpApi> = Awaited<ReturnType<CdpApi[M]>>;

/**
 * TanStack Query wrapper for CDP reads. Query key is `[method, params]`,
 * matching the prior ts-rest hooks so existing invalidations keep working.
 *
 * @public
 */
export function useCdpQuery<M extends keyof CdpApi, TData = CdpData<M>>(
  method: M,
  params: CdpArgs<M>,
  options?: Omit<UseQueryOptions<CdpData<M>, Error, TData>, 'queryKey' | 'queryFn'>,
) {
  return useTanStackQuery<CdpData<M>, Error, TData>({
    queryKey: [method, params],
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    queryFn: () => cdpApi[method](params as never) as Promise<CdpData<M>>,
    retry: shouldRetry,
    ...options,
  });
}

/** @public */
export function useCdpMutation<M extends keyof CdpApi>(
  method: M,
  options?: Omit<UseMutationOptions<CdpData<M>, Error, CdpArgs<M>>, 'mutationFn'>,
) {
  return useTanStackMutation<CdpData<M>, Error, CdpArgs<M>>({
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    mutationFn: (variables) => cdpApi[method](variables as never) as Promise<CdpData<M>>,
    ...options,
  });
}
